import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  ArrowLeftIcon,
  BarChartIcon,
  CodeIcon,
  Cross2Icon,
  ExitIcon,
  FileTextIcon,
  HamburgerMenuIcon,
  IdCardIcon,
  ImageIcon,
  LinkBreak2Icon,
  MagicWandIcon,
  MixerHorizontalIcon,
  PersonIcon,
  SpeakerLoudIcon,
  StackIcon,
  CardStackIcon,
  CubeIcon,
} from '@radix-ui/react-icons';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { ROUTES } from '@/shared/config/routes';
import { useExperimentalFeatures } from '@/shared/hooks/use-experimental-features';

interface MenuItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

export const MobileNav = () => {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuthStore();

  // Round-3 fix: mobile bottom nav previously exposed only 3 items (Playground,
  // Team, Usage) and the "More" drawer mirrored the same 3 — leaving Quickstart,
  // Characters, Styles, API Keys, Webhooks, Billing with no mobile nav surface
  // at all. Now the bottom strip carries 3 high-frequency destinations + a
  // "More" trigger; the drawer carries the full sidebar item list.
  const bottomItems: MenuItem[] = [
    {
      label: 'Playground',
      href: ROUTES.DASHBOARD_PLAYGROUND,
      icon: <ImageIcon className="w-5 h-5" />,
    },
    {
      label: 'Characters',
      href: ROUTES.DASHBOARD_CHARACTERS,
      icon: <PersonIcon className="w-5 h-5" />,
    },
    {
      label: 'Usage',
      href: ROUTES.DASHBOARD_USAGE,
      icon: <BarChartIcon className="w-5 h-5" />,
    },
  ];

  const experimental = useExperimentalFeatures();
  const drawerItems: MenuItem[] = [
    {
      label: 'Playground',
      href: ROUTES.DASHBOARD_PLAYGROUND,
      icon: <ImageIcon className="w-5 h-5" />,
    },
    {
      label: 'Quickstart',
      href: ROUTES.DASHBOARD_QUICKSTART,
      icon: <CodeIcon className="w-5 h-5" />,
    },
    {
      label: 'Characters',
      href: ROUTES.DASHBOARD_CHARACTERS,
      icon: <PersonIcon className="w-5 h-5" />,
    },
    {
      // Styles gallery (style-registers PR-2 Task 3) — MagicWandIcon matches
      // the gallery's own EmptyState icon and the sidebar's entry above.
      label: 'Styles',
      href: ROUTES.DASHBOARD_LORAS,
      icon: <MagicWandIcon className="w-5 h-5" />,
    },
    // Prism Stories after Styles, same as the sidebar (power-user review).
    // The experimental surfaces, only while the API runs with EXPERIMENTAL_FEATURES_ENABLED=true.
    ...(experimental
      ? [
          { label: 'Stories', href: ROUTES.DASHBOARD_STORIES, icon: <FileTextIcon className="w-5 h-5" /> },
          { label: 'Ads', href: ROUTES.DASHBOARD_ADS, icon: <SpeakerLoudIcon className="w-5 h-5" /> },
          { label: 'Slideshows', href: ROUTES.DASHBOARD_SLIDESHOWS, icon: <StackIcon className="w-5 h-5" /> },
          { label: 'Carousels', href: ROUTES.DASHBOARD_CAROUSELS, icon: <CardStackIcon className="w-5 h-5" /> },
          { label: 'Packages', href: ROUTES.DASHBOARD_PACKAGES, icon: <CubeIcon className="w-5 h-5" /> },
        ]
      : []),
    {
      label: 'Team',
      href: ROUTES.DASHBOARD_TEAM,
      icon: <PersonIcon className="w-5 h-5" />,
    },
    {
      label: 'API Keys',
      href: ROUTES.DASHBOARD_API_KEYS,
      icon: <MixerHorizontalIcon className="w-5 h-5" />,
    },
    {
      label: 'Webhooks',
      href: ROUTES.DASHBOARD_WEBHOOKS,
      icon: <LinkBreak2Icon className="w-5 h-5" />,
    },
    {
      label: 'Usage',
      href: ROUTES.DASHBOARD_USAGE,
      icon: <BarChartIcon className="w-5 h-5" />,
    },
    {
      label: 'Billing',
      href: ROUTES.DASHBOARD_BILLING,
      icon: <IdCardIcon className="w-5 h-5" />,
    },
  ];

  const NavItem = ({ item }: { item: MenuItem }) => {
    const isActive = location.pathname === item.href;

    return (
      <Link
        to={item.href}
        className={clsx(
          'flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-all',
          isActive ? 'text-primary' : 'text-muted-foreground',
        )}
        onClick={() => setIsMoreMenuOpen(false)}
      >
        {item.icon}
        <span className="text-xs mt-1">{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="mobile-nav fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border px-4 lg:hidden">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5 flex-1">
            {bottomItems.map((item) => (
              <NavItem key={item.href} item={item} />
            ))}
          </div>

          {/* More Button — opens drawer with the full menu */}
          <button
            onClick={() => setIsMoreMenuOpen(true)}
            className="flex flex-col items-center justify-center py-2 px-3 rounded-lg text-muted-foreground cursor-pointer"
          >
            <HamburgerMenuIcon className="w-5 h-5" />
            <span className="text-xs mt-1">More</span>
          </button>
        </div>
      </nav>

      {/* Full-screen More Menu */}
      <AnimatePresence>
        {isMoreMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsMoreMenuOpen(false)}
            />

            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-y-0 left-0 w-full max-w-sm bg-card z-50 overflow-y-auto lg:hidden"
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-semibold text-white">Menu</h2>
                  <button
                    onClick={() => setIsMoreMenuOpen(false)}
                    className="p-2 rounded-lg text-muted-foreground hover:text-white hover:bg-secondary transition-colors cursor-pointer"
                  >
                    <Cross2Icon className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-2">
                  {/* Navigation Items — full sidebar parity, including
                      Quickstart / Styles / API Keys / Webhooks / Billing
                      that the bottom strip can't fit. */}
                  {drawerItems.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsMoreMenuOpen(false)}
                        className={clsx(
                          'flex items-center gap-3 px-4 py-3 rounded-xl transition-all',
                          'hover:bg-secondary',
                          isActive && 'bg-primary text-white',
                          !isActive && 'text-muted-foreground hover:text-white',
                        )}
                      >
                        {item.icon}
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}

                  {/* Divider */}
                  <div className="my-4 border-t border-border" />

                  {/* Login/Logout Button */}
                  {isAuthenticated ? (
                    <button
                      onClick={() => {
                        setIsMoreMenuOpen(false);
                        logout();
                        navigate(ROUTES.HOME);
                        window.location.reload();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors font-medium cursor-pointer"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  ) : (
                    <Link
                      to={ROUTES.AUTH.REGISTER}
                      onClick={() => setIsMoreMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:bg-secondary text-muted-foreground hover:text-white"
                    >
                      <ExitIcon className="w-5 h-5" />
                      <span className="font-medium">Sign In</span>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
